import { useCallback, useContext, useState } from "react";
import { useLocation } from "react-router-dom";

import { NotesBlueIcon } from "../../../../assets";
import { InteractionPermissionConstants, ProjectPermissionConstants, UserType } from "../../../../constants";
import { CursorChangeProps, ResultType } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { searchProjects, SearchProjectsResponse } from "../../../../service/publicquery";
import { GetGroupDetailsResponse } from "../../../../service/query";
import { ResultData } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { flattenObject } from "../../../../utils";
import { getProjectDetailsByUuid } from "../../../../utils/routes";
import { DataGridLinkCellFormatterData, dataGridMapFilterCriteria } from "../../../../widget";
import { IconCellFormatterData, OnCursorChangeEventHandler } from "../../../../widget/data/DataGrid/models";
import { LandholdingContext } from "../LandholdingContext";

interface UseDashboardReturnData {
  currentUserType: UserType;
  landholdingDetails?: GetGroupDetailsResponse;
  isProjectsGridLoading: boolean;
  projectsOnChange: OnCursorChangeEventHandler;
  notesProjectUuid?: string;
  showNotesPanel: boolean;
  closeNotesPanel: () => void;
  readGroupProjectsPermission: ProjectPermissionConstants | undefined;
}

export const useDashboard = (): UseDashboardReturnData => {
  const location = useLocation();

  const { landholdingUuid, landholdingDetails, currentUserProjectPermissionsResponse, readGroupProjectsPermission } =
    useContext(LandholdingContext);
  const { currentUserType } = useAuth();

  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [notesProjectUuid, setNotesProjectUuid] = useState<string>();

  const [isProjectsGridLoading, setIsProjectsGridLoading] = useState(true);

  const closeNotesPanel = useCallback((): void => {
    setNotesProjectUuid(undefined);
    setShowNotesPanel(false);
  }, []);

  // --- For projects data grid ---
  const formatProjectsData = useCallback(
    (responseData: SearchProjectsResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.access = "No";

        result.link = {
          text: el.displayName,
          to: getProjectDetailsByUuid(el.uuid, currentUserType),
          state: { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin },
        } as DataGridLinkCellFormatterData;
        if (
          currentUserProjectPermissionsResponse?.some(
            (x) =>
              x.projectUuid === el.uuid &&
              x.currentUserPermissions.includes(InteractionPermissionConstants.MANAGE_INTERACTION)
          )
        ) {
          result.notesButton = {
            action: () => {
              setNotesProjectUuid(el.uuid);
              setShowNotesPanel(true);
            },
            icon: <NotesBlueIcon />,
          } as IconCellFormatterData;
        }
        if (
          currentUserProjectPermissionsResponse?.some(
            (x) =>
              x.projectUuid === el.uuid && x.currentUserPermissions.includes(ProjectPermissionConstants.READ_PROJECT)
          )
        )
          result.access = "Yes";

        return result;
      }) || [],
    [currentUserType, location.pathname, currentUserProjectPermissionsResponse]
  );

  const projectsOnChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    setIsProjectsGridLoading(true);
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (filterCriteria.link) {
      filterCriteria.displayName = filterCriteria.link;
      filterCriteria.link = undefined;
    }

    filterCriteria.landholdings = {
      some: {
        groupUuid: {
          operator: "eq",
          value: landholdingUuid,
        },
      },
    };

    await searchProjects({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: paging.pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: s.key !== "link" ? s.key : "displayName",
        direction: s.direction,
      })),
      /* eslint-enable */
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatProjectsData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsProjectsGridLoading(false);
      });

    return data;
  };

  return {
    currentUserType,
    landholdingDetails,

    notesProjectUuid,

    showNotesPanel,
    closeNotesPanel,

    isProjectsGridLoading,
    projectsOnChange,
    readGroupProjectsPermission,
  };
};
